import i18next from 'i18next';
import head from 'lodash/head';
import last from 'lodash/last';
import map from 'lodash/map';
import EventEmitter from 'wolfy87-eventemitter';
import { PRICES_EVENTS } from '../../../constants/CONTROLLERS_EVENT_TYPES';
import { PricePreview } from './prices.interface';
import { inject } from 'inversify';
import { STATE_MODULE_IDS } from '../state/state.types';
import type { IStateController } from '../state/state.interface';
import { TICKETS_MODULE_IDS } from '../tickets/tickets.types';
import type { ITicketsController } from '../tickets/tickets.interface';

function renderNotification() {
  if (window.Widget.schemeMode) {
    window.Widget.notificationController.info({
      title: i18next.t('hint'),
      message: i18next.t('hintText'),
    });
  }
}

export class PricesController extends EventEmitter {
  prices: Array<Array<number>> = [];

  colors: Array<string> = [
    '#FFA800',
    '#65BCE1',
    '#DE665E',
    '#38D080',
    '#B186D3',
    '#977474',
    '#6580E1',
    '#ECDD55',
  ];

  pricesColors: Object = [];

  pricesPreview: Array<PricePreview> = [];

  pricesIndexes: Object = {};

  selectedPrices: Set<Number> = new Set();

  haveToShowNotification: boolean = false;

  @inject(STATE_MODULE_IDS.STATE_CONTROLLER) public stateController: IStateController;
  @inject(TICKETS_MODULE_IDS.TICKETS_CONTROLLER) public ticketsController: ITicketsController;

  togglePrice(index: Number) {
    const { selectedPrices } = this;

    if (selectedPrices.has(index)) {
      selectedPrices.delete(index);
    } else {
      selectedPrices.add(index);
    }

    this.emit(PRICES_EVENTS.TOGGLE_PRICE);
  }

  clearPrices() {
    this.selectedPrices.clear();

    this.emit(PRICES_EVENTS.TOGGLE_PRICE);
  }

  setPricesPreview(prices: Array<Array<number>>) {
    this.prices = prices;
    this.pricesPreview = [];
    this.pricesColors = this.prices.reduce((acc, priceArray, index) => {
      priceArray.forEach((price) => {
        acc[price] = this.colors[index];
        this.pricesIndexes[price] = index;
      });

      const firstPrice = head(priceArray);
      const lastPrice = last(priceArray);

      this.pricesPreview.push({
        tooltip: firstPrice !== lastPrice ? `${firstPrice} - ${lastPrice}` : `${firstPrice}`,
        value: firstPrice,
        color: this.colors[index],
        index,
      });
      return acc;
    }, {});
    this.emit(PRICES_EVENTS.PRICES_PREVIEW_UPDATED);
  }

  updatePrices(prices: Array<Array<number>>) {
    this.setPricesPreview(prices);
    const { leftoversSectors } = this.ticketsController;

    if (leftoversSectors) {
      this.ticketsController.leftoversSectors = Object.keys(leftoversSectors).reduce(
        (acc, sectorName) => {
          acc[sectorName] = {
            ...leftoversSectors[sectorName],
            color: this.pricesColors[leftoversSectors[sectorName].minPrice],
          };

          return acc;
        },
        {},
      );

      this.tryNotification();
    }
  }

  updateCategoriesPrices(prices: Array<Array<number>>, colors: any) {
    this.prices = prices;
    this.pricesColors = colors;
    this.colors = map(colors, (value) => value);
    this.pricesPreview = [];

    this.prices.reduce((acc, priceArray, index) => {
      priceArray.forEach((price) => {
        acc[price] = this.colors[index];
        this.pricesIndexes[price] = index;
      });

      const firstPrice = head(priceArray);
      const lastPrice = last(priceArray);

      this.pricesPreview.push({
        tooltip: firstPrice !== lastPrice ? `${firstPrice} - ${lastPrice}` : `${firstPrice}`,
        value: firstPrice,
        color: this.colors[index],
        index,
      });

      return acc;
    }, {});

    this.emit(PRICES_EVENTS.UPDATE);
  }

  tryNotification() {
    if (!window.Widget.isMobile && !this.stateController.state.isFullscreen) {
      renderNotification();
      return;
    }

    this.stateController.addListener(this.stateController.events.UPDATE, ({ isFullscreen }) => {
      if (isFullscreen && this.haveToShowNotification) {
        renderNotification();
        this.haveToShowNotification = false;
      }
    });

    this.haveToShowNotification = true;
  }

  resetSelectedPrices() {
    this.selectedPrices = new Set();
    this.emit(PRICES_EVENTS.TOGGLE_PRICE);
  }

  setSchemePrices() {
    if (window.Widget.withCategories) return;

    const pricesSet = new Set(Object.keys(this.pricesIndexes).map((i) => Number(i)));
    const prevPrices = this.ticketsController.getPrices(pricesSet);

    this.setPricesPreview(prevPrices);
  }
}
